/**
 * Dashboard Date Range Picker
 *
 * Date range picker with preset buttons for the historical performance chart.
 * Adapted from QA Reports DateRangePicker with production-specific presets.
 *
 * Includes client-side validation to match backend 90-day limit.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, subDays, differenceInDays } from 'date-fns';
import { Calendar as CalendarIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { DateRange, DatePreset } from '../types';

/** Maximum date range allowed (must match backend RPC limit) */
const MAX_DATE_RANGE_DAYS = 90;

// Production-specific presets: 7, 14, 30 days
const PRESETS: DatePreset[] = [
  {
    label: '7 days',
    getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }),
  },
  {
    label: '14 days',
    getValue: () => ({ from: subDays(new Date(), 14), to: new Date() }),
  },
  {
    label: '30 days',
    getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }),
  },
];

interface DashboardDateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

export function DashboardDateRangePicker({
  value,
  onChange,
  className,
}: DashboardDateRangePickerProps) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{ from: Date; to?: Date } | null>(null);

  // Check if current value matches a preset (for highlighting active preset)
  const getActivePreset = (): string | null => {
    const today = new Date();
    const daysDiff = Math.round(
      (today.getTime() - value.from.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Check if 'to' is today (or very close)
    const toIsToday =
      Math.abs(value.to.getTime() - today.getTime()) < 24 * 60 * 60 * 1000;

    if (!toIsToday) return null;

    if (daysDiff === 7) return '7 days';
    if (daysDiff === 14) return '14 days';
    if (daysDiff === 30) return '30 days';

    return null;
  };

  const activePreset = getActivePreset();

  // Track validation error for custom range
  const [rangeError, setRangeError] = useState<string | null>(null);

  const handlePresetClick = (preset: DatePreset) => {
    setRangeError(null);
    setDraft(null);
    onChange(preset.getValue());
  };

  const handleRangeSelect = (range: { from?: Date; to?: Date } | undefined) => {
    if (!range?.from) {
      setDraft(null);
      return;
    }
    if (range.from && range.to) {
      // Validate 90-day limit (matches backend RPC)
      const daysDiff = differenceInDays(range.to, range.from);
      if (daysDiff > MAX_DATE_RANGE_DAYS) {
        setRangeError(t('dashboard.dateRange.maxExceeded', { max: MAX_DATE_RANGE_DAYS }));
        setDraft(null);
        return;
      }
      setRangeError(null);
      setDraft(null);
      onChange({ from: range.from, to: range.to });
      setOpen(false);
    } else {
      setRangeError(null);
      setDraft({ from: range.from });
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setDraft(null);
      setRangeError(null);
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Preset buttons - visible on larger screens */}
      <div className="hidden sm:flex items-center gap-1">
        {PRESETS.map((preset) => (
          <Button
            key={preset.label}
            variant={activePreset === preset.label ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePresetClick(preset)}
            className="h-8 px-3 text-xs"
          >
            {preset.label}
          </Button>
        ))}
      </div>

      {/* Calendar popover for custom range */}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'h-8 justify-start text-left font-normal',
              // On mobile, show full date range. On desktop, just show calendar icon
              'sm:w-auto',
              !value && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="h-4 w-4 sm:mr-0 mr-2" />
            {/* Show date range on mobile, hide on desktop (presets visible instead) */}
            <span className="sm:hidden">
              {format(value.from, 'MMM d')} - {format(value.to, 'MMM d')}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 flex flex-col sm:flex-row" align="end">
          {/* Presets sidebar - shown in popover for mobile */}
          <div className="border-b sm:border-b-0 sm:border-r p-2 space-y-1 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-x-visible">
            {PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant={activePreset === preset.label ? 'secondary' : 'ghost'}
                size="sm"
                className="w-full justify-start whitespace-nowrap"
                onClick={() => {
                  handlePresetClick(preset);
                  setOpen(false);
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          {/* Calendar */}
          <div className="p-2">
            <Calendar
              mode="range"
              selected={draft ?? { from: value.from, to: value.to }}
              onSelect={handleRangeSelect}
              numberOfMonths={2}
              defaultMonth={draft?.from ?? value.from}
              disabled={{ after: new Date() }} // Can't select future dates
            />
            {/* Validation error message */}
            {rangeError && (
              <div className="flex items-center gap-2 text-destructive text-sm mt-2 px-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{rangeError}</span>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
